// Spider (one / two / four suits). .ai/game-rules.md "Spider".
// Product decision (2026-10-07): the suits of one-suit and two-suits VARY PER DEAL but are derived
// from the deal's own shuffle pattern (suitsFromPattern), so a replayed pattern always deals the same
// cards (Retry, stored patterns, multiplayer). Legacy picked them at random per page.
import type { Card, CardKey, GameId, GameState, PileRef, Suit } from '../types';
import { rankLow } from '../cards';
import { card, pileCards, topOf, type VariantRules } from './base';

/** Down by 1, ANY suit. An Ace goes on a 2; nothing goes on an Ace; a King only to an empty column. */
function buildsOn(c: Card, onto: Card): boolean {
  return rankLow(onto.rank) === rankLow(c.rank) + 1;
}

/** The run from `index` to the bottom of the column is same-suit, descending by 1, all face up. */
function canLift(s: GameState, pile: PileRef, index: number): boolean {
  if (pile.kind !== 'tableau') return false;
  const col = pileCards(s, pile);
  if (index < 0 || index >= col.length) return false;
  for (let i = index; i < col.length; i++) {
    const key = col[i]!;
    if (!s.faceUp[key]) return false;
    if (i > index) {
      const above = card(s, col[i - 1]!);
      const c = card(s, key);
      if (c.suit !== above.suit || rankLow(above.rank) !== rankLow(c.rank) + 1) return false;
    }
  }
  return true;
}

function canDrop(s: GameState, run: readonly CardKey[], target: PileRef): boolean {
  const bottom = run[0];
  if (bottom === undefined || target.kind !== 'tableau') return false; // never a manual foundation move
  const top = topOf(pileCards(s, target));
  if (top === undefined) return true;
  return buildsOn(card(s, bottom), card(s, top));
}

function completedRun(s: GameState, column: number): boolean {
  const col = s.piles.tableau[column];
  if (!col || col.length < 13) return false;
  const run = col.slice(-13);
  const suit = card(s, run[0]!).suit;
  for (let i = 0; i < 13; i++) {
    const c = card(s, run[i]!);
    if (!s.faceUp[run[i]!] || c.suit !== suit || rankLow(c.rank) !== 13 - i) return false;
  }
  return true;
}

/**
 * The suits of a one-suit / two-suits deal, from a hash of its pattern: one suit = one of
 * club, spade, heart, diamond; two suits = one black and one red, in the legacy `deck_colors`
 * order [black, red, black, red].
 */
export function suitsFromPattern(pattern: readonly number[], variable: 1 | 2): Suit[] {
  let h = 0x9e3779b9;
  for (let i = 0; i < pattern.length; i++) h = Math.imul(h ^ (pattern[i]! + i), 0x85ebca6b) >>> 0;
  // Finalizer (murmur3 style) so the bits used below depend on every entry.
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  const pick = (h >>> 8) & 0xff;
  if (variable === 1) {
    const suit = (['c', 's', 'h', 'd'] as const)[pick % 4]!;
    return [suit, suit, suit, suit];
  }
  const black = (['c', 's'] as const)[pick % 2]!;
  const red = (['h', 'd'] as const)[(pick >>> 1) % 2]!;
  return [black, red, black, red];
}

function make(id: GameId, suits: Suit[], variableSuits: 0 | 1 | 2): VariantRules {
  return {
    id,
    columns: [6, 6, 6, 6, 5, 5, 5, 5, 5, 5],
    foundations: 8,
    cells: 0,
    decks: 2,
    suits,
    variableSuits,
    turn: 'spider',
    hasWaste: false,
    recycle: false,
    allFaceUp: false,
    autoMove: false,
    foundationToTableau: false,
    canLift,
    canDrop,
    foundationFor: () => null,
    buildsOn,
    completedRun,
  };
}

/** The listed suits are the defaults before the first deal; each deal derives its own. */
export const spiderOneSuit = make('spider:one-suit', ['s', 's', 's', 's'], 1);
export const spiderTwoSuits = make('spider:two-suits', ['s', 'h', 's', 'h'], 2);
/** Legacy `deck_colors` order for four suits: club, spade, heart, diamond (types/spider.js:77). */
export const spiderFourSuits = make('spider:four-suits', ['c', 's', 'h', 'd'], 0);
